"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CartDrawer = void 0;
const cart_api_1 = require("../../api/cart-api");
const fetch_config_1 = require("../../utils/fetch-config");
const sidebar_1 = require("../../components/sidebar/sidebar");
const dom_1 = require("../../utils/dom");
const debounce_1 = require("../../utils/debounce");
const utils_1 = require("../../utils/utils");
const transition_util_1 = require("../../utils/transition-util");
const VIEWPORT_SELECTOR = '[data-cart-drawer-viewport]';
const RESULTS_SELECTOR = '[data-cart-drawer-results]';
const NOTIFICATION_SELECTOR = '#CartDrawerNotification';
const CART_ITEM_SELECTOR = 'cart-item';
class CartDrawer extends sidebar_1.SidebarComponent {
    cartAPI = new cart_api_1.CartAPI();
    resizeObserver;
    lastDrawerBody;
    error;
    mountComponent() {
        super.mountComponent();
        const viewport = (0, utils_1.$el)(VIEWPORT_SELECTOR, this.element);
        const results = (0, utils_1.$el)(RESULTS_SELECTOR, this.element);
        if (results) {
            this.resizeObserver = new ResizeObserver(this.handleResultsResize);
            this.resizeObserver.observe(results);
        }
        this.addListener(this, 'change', this.handleCartChange);
        this.addListener(this, 'change', this.handleItemChange);
        this.addListener(viewport, 'scroll', this.handleViewportScroll);
    }
    unmountComponent() {
        super.unmountComponent();
        this.resizeObserver.disconnect();
    }
    handleItemChange = ({ target }) => {
        const item = (0, utils_1.$el)(`#CartItem-${+target.dataset.index}`, this);
        item?.setLoading(true);
        this.setLoadingStatus(true);
    };
    handleCartChange = (0, debounce_1.debounce)(({ target }) => {
        this.updateItem(+target.dataset.index, +target.value);
    }, 500);
    handleViewportScroll = (0, debounce_1.debounce)(() => {
        this.updateViewport();
    }, 25);
    handleResultsResize = (0, debounce_1.debounce)(() => {
        this.updateViewport();
    }, 25);
    updateViewport() {
        const results = (0, utils_1.$el)(RESULTS_SELECTOR, this.element);
        const viewport = (0, utils_1.$el)(VIEWPORT_SELECTOR, this.element);
        if (!results || !viewport) {
            return;
        }
        const isOverflow = results.offsetHeight > viewport.offsetHeight;
        const isScrolled = viewport.scrollTop > 0;
        this.classList.toggle('header-shadow-visible', isOverflow && isScrolled);
        this.classList.toggle('footer-shadow-visible', isOverflow);
    }
    update() {
        this.cartAPI
            .getDrawer()
            .then(responseText => {
            this.lastDrawerBody = this.getTemplateContent((0, dom_1.parseHTML)(responseText));
        })
            .catch(() => {
            this.showError('');
        })
            .finally(() => {
            this.mount();
            this.emit('update-nodes', {
                node: this.lastDrawerBody,
            });
            this.updateEmptyStatus();
        });
    }
    updateItem(itemIndex, quantity) {
        const item = (0, utils_1.$el)(`#CartItem-${itemIndex}`, this);
        if (!item) {
            return;
        }
        this.setLoadingStatus(true);
        const currentQuantity = +(item.dataset.quantity || 0);
        if (currentQuantity === quantity) {
            item.setLoading(false);
            this.setLoadingStatus(false);
            return;
        }
        item.setDisabled(true);
        if (quantity === 0) {
            item.removeItem();
            (0, transition_util_1.whenTransitionEnds)(item, () => {
                const items = (0, utils_1.$list)(CART_ITEM_SELECTOR, this.element);
                this.setPreEmpty(items.length === 1);
            });
        }
        this.emit('start-update', {});
        this.cartAPI
            .change({
            ...(0, fetch_config_1.fetchConfig)(),
            body: JSON.stringify({
                line: itemIndex + 1,
                quantity,
                sections: this.dataset.sectionId,
                sections_url: window.location.pathname,
            }),
        })
            .then(state => {
            const data = JSON.parse(state);
            if (data.errors) {
                throw new Error(data.errors);
            }
            this.lastDrawerBody = this.getTemplateContent((0, dom_1.parseHTML)(data.sections[this.dataset.sectionId]));
        })
            .catch(error => {
            this.error = error;
        })
            .finally(() => {
            this.mount();
            this.emit('update-nodes', {
                node: this.lastDrawerBody,
            });
            this.updateEmptyStatus();
            this.initFocus();
            item.setLoading(false);
            this.setLoadingStatus(false);
            item.setDisabled(false);
            if (this.error) {
                this.showError(this.error);
                this.error = null;
            }
        });
    }
    setLoadingStatus(isLoading) {
        this.classList.toggle('is-cart-loading', isLoading);
    }
    showError(text) {
        const notification = (0, utils_1.$el)(NOTIFICATION_SELECTOR, this);
        const message = text ? text : window.lbcThemeLocales.cartStrings.error;
        notification?.show(message, 'warning', 6000);
    }
    async purchaseHandler(html, parsedState) {
        this.setPreEmpty(false);
        const node = this.getTemplateContent(html);
        this.mount();
        this.emit('update-nodes', { node, parsedState });
        this.updateEmptyStatus();
        await this.open();
        this.emit('purchase', { parsedState });
        this.addListener(this, 'showAsNew', this.updateLastDrawerBody);
    }
    updateLastDrawerBody = () => {
        this.lastDrawerBody = this.element.cloneNode(true);
    };
    setPreEmpty(isEmpty) {
        this.classList.toggle('is-pre-empty', isEmpty);
    }
    getTemplateContent(html) {
        const newTemplate = (0, utils_1.$el)(`[data-sidebar-template="${this.id}"]`, html);
        return (0, dom_1.getTemplateFirstChild)(newTemplate);
    }
    updateEmptyStatus() {
        const items = (0, utils_1.$list)(CART_ITEM_SELECTOR, this.element);
        if (this.hasAttribute('data-has-warning-placeholder')) {
            this.toggleAttribute('is-empty', items.length === 0);
        }
        if (items.length === 0) {
            this.emit('cartIsEmpty', {});
        }
    }
    updateAsideProductsByParsedState(parsedState) {
        this.toggleAttribute('has-aside-products', parsedState.quantity > 0);
    }
    async open() {
        await super.open();
        this.lastDrawerBody = this.element.cloneNode(true);
    }
}
exports.CartDrawer = CartDrawer;
